import * as THREE from 'three';
import { configureShadowRenderer, configureShadowLight } from './shadowConfig';
import { updateRayTracingLighting, RAY_TRACING_CONFIG, invalidatePhotonMap } from './rayTracing';

let lastPhotonMapSunPosition: THREE.Vector3 | null = null;

const getSunPosition = (dateTime: Date, latitude: number, longitude: number): THREE.Vector3 => {
	const lat = (latitude * Math.PI) / 180;

	const start = new Date(dateTime.getFullYear(), 0, 0);
	const diff = dateTime.getTime() - start.getTime();
	const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));

	const localTimeOfDay =
		dateTime.getHours() + dateTime.getMinutes() / 60 + dateTime.getSeconds() / 3600;

	// Solar declination angle (Earth's tilt throughout the year)
	const declinationAngle = 23.45 * Math.sin((((360 * (284 + dayOfYear)) / 365) * Math.PI) / 180);
	const declination = (declinationAngle * Math.PI) / 180;

	// Longitude correction for solar time
	// Each degree of longitude = 4 minutes of time difference from GMT
	// Positive longitude (East) = earlier solar time
	// Negative longitude (West) = later solar time
	const longitudeTimeCorrection = longitude / 15; // Convert degrees to hours

	// Equation of time (accounts for Earth's elliptical orbit)
	const B = (2 * Math.PI * (dayOfYear - 81)) / 365;
	const equationOfTime = 9.87 * Math.sin(2 * B) - 7.53 * Math.cos(B) - 1.5 * Math.sin(B);
	const equationOfTimeHours = equationOfTime / 60; // Convert minutes to hours

	// Calculate true solar time
	// For locations WEST of prime meridian (negative longitude), we ADD the correction
	// For locations EAST of prime meridian (positive longitude), we SUBTRACT the correction
	const solarTime = localTimeOfDay + longitudeTimeCorrection + equationOfTimeHours;

	// Hour angle (0° at solar noon, +15° per hour after noon, -15° per hour before noon)
	const hourAngle = ((solarTime - 12) * 15 * Math.PI) / 180;

	// Solar elevation angle (altitude above horizon)
	const elevation = Math.asin(
		Math.sin(declination) * Math.sin(lat) +
			Math.cos(declination) * Math.cos(lat) * Math.cos(hourAngle)
	);

	// Solar azimuth angle (compass direction)
	const azimuthRaw = Math.atan2(
		Math.sin(hourAngle),
		Math.cos(hourAngle) * Math.sin(lat) - Math.tan(declination) * Math.cos(lat)
	);

	// Convert azimuth to standard compass bearing (0° = North, 90° = East)
	let azimuth = azimuthRaw + Math.PI;
	if (azimuth > 2 * Math.PI) azimuth -= 2 * Math.PI;

	// Convert to 3D Cartesian coordinates for Three.js
	const distance = 1000;
	const x = distance * Math.sin(azimuth) * Math.cos(elevation);
	const y = distance * Math.cos(azimuth) * Math.cos(elevation);
	const z = distance * Math.sin(elevation);

	const position = new THREE.Vector3(x, y, z);

	return position;
};

const createDateTimeFromString = (date: Date, timeString: string): Date => {
	const [hours, minutes] = timeString.split(':').map(Number);
	const newDate = new Date(date);
	newDate.setHours(hours, minutes, 0, 0);

	return newDate;
};

const calculateSunPositionFromDateTime = (
	dateTime: Date,
	latitude: number,
	longitude: number
): { elevation: number; azimuth: number; position: THREE.Vector3 } => {
	const sunPosition3D = getSunPosition(dateTime, latitude, longitude);
	const distance = sunPosition3D.length();
	const elevation = Math.asin(sunPosition3D.z / distance);
	const azimuth = Math.atan2(sunPosition3D.x, sunPosition3D.y);

	return {
		elevation,
		azimuth,
		position: sunPosition3D,
	};
};

export const updateLighting = (
	scene: THREE.Scene,
	ambientLight: THREE.AmbientLight,
	directionalLight: THREE.DirectionalLight,
	renderer: THREE.WebGLRenderer,
	starsRef: THREE.Points | null,
	currentSunPositionRef: React.MutableRefObject<THREE.Vector3>,
	currentDate: Date,
	currentTime: string,
	latitude: number | null,
	longitude: number | null,
	skipShadowConfig?: boolean
): void => {
	if (
		!scene ||
		!ambientLight ||
		!directionalLight ||
		!renderer ||
		latitude === null ||
		longitude === null
	)
		return;

	configureShadowRenderer(renderer);

	const dateTime = createDateTimeFromString(currentDate, currentTime);
	const sunData = calculateSunPositionFromDateTime(dateTime, latitude, longitude);
	const sunElevationDeg = THREE.MathUtils.radToDeg(sunData.elevation);
	const isActuallyDay = sunElevationDeg > -6;

	currentSunPositionRef.current.copy(sunData.position);

	if (isActuallyDay) {
		const skyIntensity = Math.max(0.4, Math.sin(sunData.elevation));

		if (sunElevationDeg < 10 && sunElevationDeg > -6) {
			const skyColor = new THREE.Color().setHSL(0.08, 0.8, 0.6 + skyIntensity * 0.3);
			scene.background = skyColor;
			renderer.setClearColor(skyColor);
		} else {
			const skyColor = new THREE.Color().setHSL(0.55, 0.7, 0.6 + skyIntensity * 0.4);
			scene.background = skyColor;
			renderer.setClearColor(skyColor);
		}

		if (starsRef) starsRef.visible = false;
	} else {
		const darkColor = new THREE.Color(0x001122);
		scene.background = darkColor;
		renderer.setClearColor(darkColor, 1.0);
		if (starsRef) starsRef.visible = true;
	}

	const ambientIntensity = isActuallyDay
		? Math.max(0.15, Math.sin(sunData.elevation) * 0.35)
		: 0.02;
	ambientLight.intensity = ambientIntensity;

	if (isActuallyDay) {
		if (!skipShadowConfig) {
			scene.updateMatrixWorld(true);

			const sceneBounds = new THREE.Box3();
			scene.traverse((child) => {
				if (
					child instanceof THREE.Mesh &&
					child.visible &&
					(child.userData.category === 'building' ||
						child.userData.category === 'terrain' ||
						child.userData.category === 'vegetation') &&
					!child.userData.isShadowIndicator
				) {
					const meshBox = new THREE.Box3().setFromObject(child);
					if (!meshBox.isEmpty()) {
						sceneBounds.union(meshBox);
					}
				}
			});

			if (!sceneBounds.isEmpty()) {
				const center = new THREE.Vector3();
				sceneBounds.getCenter(center);
				const size = new THREE.Vector3();
				sceneBounds.getSize(size);

				const distance = Math.max(size.x, size.y, size.z) * 3;
				const sunPosition = sunData.position.clone().normalize();
				sunPosition.multiplyScalar(distance);
				sunPosition.add(center);

				directionalLight.position.copy(sunPosition);
				directionalLight.target.position.copy(center);
				directionalLight.target.updateMatrixWorld();

				configureShadowLight(directionalLight, sceneBounds, distance);
			}
		}

		const elevationFactor = Math.max(0.3, Math.sin(sunData.elevation));
		directionalLight.intensity = 1.8 + elevationFactor * 1.8;

		if (sunElevationDeg < 10) {
			directionalLight.color.setHSL(0.08, 0.8, 0.8);
		} else if (sunElevationDeg < 30) {
			directionalLight.color.setHSL(0.1, 0.3, 0.95);
		} else {
			directionalLight.color.setHSL(0, 0, 1);
		}

		directionalLight.castShadow = true;
	} else {
		directionalLight.intensity = 0.05;
		directionalLight.color.setHSL(0.6, 0.2, 0.8);
		directionalLight.castShadow = false;
	}

	directionalLight.shadow.needsUpdate = true;
	renderer.shadowMap.needsUpdate = true;

	// Update ray tracing environment if enabled
	if (RAY_TRACING_CONFIG.enabled) {
		updateRayTracingLighting(scene, renderer, sunData.position, directionalLight.intensity);

		const sunPosition3D = getSunPosition(dateTime, latitude, longitude);
		currentSunPositionRef.current = sunPosition3D;

		if (lastPhotonMapSunPosition) {
			invalidatePhotonMap();
			lastPhotonMapSunPosition = null;
		}
	}
};

export { getSunPosition };
